import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert as MuiAlert,
  CircularProgress,
} from '@mui/material';
import { useApi } from '../../hooks/useApi';
import FulfillmentStatusSelect from './FulfillmentStatusSelect';

interface FulfillmentStatusDialogProps {
  open: boolean;
  fulfillmentStatusApiUrl: string;
  alertId: number;
  alertLabel: string | null;
  currentStatus: string | null;
  onClose: () => void;
  onSave: () => void;
}

export default function FulfillmentStatusDialog({
  open,
  fulfillmentStatusApiUrl,
  alertId,
  alertLabel,
  currentStatus,
  onClose,
  onSave,
}: FulfillmentStatusDialogProps) {
  const { patch, loading, error } = useApi();
  const [status, setStatus] = useState<string>(currentStatus ?? '');

  const handleClose = (_event: unknown, reason?: string) => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  const handleSubmit = async () => {
    if (loading) return;
    try {
      await patch(fulfillmentStatusApiUrl, { alertId, fulfillmentStatus: status });
      onSave();
    } catch (err) {
      console.error('Failed to update fulfillment status:', err);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} aria-labelledby="fulfillment-status-dialog-title" maxWidth="xs" fullWidth>
      <DialogTitle id="fulfillment-status-dialog-title">
        Edit Fulfillment Status — Alert #{alertLabel}
      </DialogTitle>
      <DialogContent>
        {error && (
          <MuiAlert severity="error" sx={{ mb: 2 }}>
            {error}
          </MuiAlert>
        )}
        <Box sx={{ mt: 1 }}>
          <FulfillmentStatusSelect
            value={status}
            onChange={setStatus}
            disabled={loading}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !status || status === (currentStatus ?? '')}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
